import React, { useState } from 'react'
import { Alert, Flex, Segmented, Table, Tag, Tooltip, Typography } from 'antd'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { CustomerFlag, CustomerRow, PeriodQuery } from '../dashboard'
import { useAdminCustomers } from '../hooks'
import { MetricTile } from './metric-tile'
import { formatMicros } from './spend-tables'
import { TimeAgo } from '../../components/time-ago'

type Filter = 'all' | CustomerFlag

// Revenue is in cents, cost in millionths of a euro.
const centsToMicros = (cents: number) => cents * 10_000

function margin(row: CustomerRow) {
    return row.revenue === null ? null : centsToMicros(row.revenue) - row.costEurMicros
}

function usageShare(row: CustomerRow) {
    return Math.max(
        ...[row.conversations, row.messages].map((usage) => (usage && usage.limit > 0 ? usage.used / usage.limit : 0))
    )
}

export function AdminCustomers(query: PeriodQuery) {
    const { data, isPending, isError } = useAdminCustomers(query)
    const { t } = useTranslation()
    const [filter, setFilter] = useState<Filter>('all')

    if (isError) {
        return (
            <Alert
                type="error"
                showIcon
                message={t('Could not load statistics for this period.')}
            />
        )
    }

    const companies = data?.companies ?? []
    const count = (flag: CustomerFlag) => companies.filter((company) => company.flags.includes(flag)).length
    const statusCount = (status: CustomerRow['status']) =>
        companies.filter((company) => company.status === status).length
    const revenue = companies.reduce((sum, company) => sum + (company.revenue ?? 0), 0)
    const cost = companies.reduce((sum, company) => sum + company.costEurMicros, 0)
    const unknownPrices = companies.filter((company) => company.revenue === null).length

    const flagLabels: Record<CustomerFlag, string> = {
        loss: t('Costs more than it pays'),
        near_limit: t('Near a limit'),
        inactive: t('Inactive'),
    }
    const flagColors: Record<CustomerFlag, string> = {
        loss: 'error',
        near_limit: 'warning',
        inactive: 'default',
    }
    const statusLabels: Record<CustomerRow['status'], string | null> = {
        paying: null,
        free: t('Free'),
        trial: t('Trial'),
        past_due: t('Past due'),
        none: t('No subscription'),
    }

    const rows = filter === 'all' ? companies : companies.filter((company) => company.flags.includes(filter))

    const planLabel = (row: CustomerRow) => {
        if (!row.tier) {
            return '—'
        }

        const tier = t(`tier.${row.tier}`)

        return row.period ? `${tier} · ${t(`period.${row.period}`)}` : tier
    }

    return (
        <Flex
            vertical
            gap="large"
        >
            <div className="overflow-hidden rounded-xl border border-hairline">
                <div className="grid grid-cols-2 gap-px bg-hairline md:grid-cols-4">
                    <MetricTile
                        loading={isPending}
                        label={t('Paying companies')}
                        value={statusCount('paying').toLocaleString()}
                        hint={t('{{trial}} on trial, {{free}} free, {{pastDue}} past due', {
                            trial: statusCount('trial'),
                            free: statusCount('free'),
                            pastDue: statusCount('past_due'),
                        })}
                    />
                    <MetricTile
                        loading={isPending}
                        label={t('Est. revenue')}
                        value={formatMicros(centsToMicros(revenue), 'EUR')}
                        hint={
                            unknownPrices > 0
                                ? t('Plan prices for this period, {{count}} without a known price', {
                                      count: unknownPrices,
                                  })
                                : t('Plan prices for this period, without extra usage')
                        }
                    />
                    <MetricTile
                        loading={isPending}
                        label={t('Spend')}
                        value={formatMicros(cost, 'EUR')}
                        hint={t('OpenAI and WhatsApp on Evaa numbers')}
                    />
                    <MetricTile
                        loading={isPending}
                        label={t('Est. margin')}
                        value={formatMicros(centsToMicros(revenue) - cost, 'EUR')}
                        hint={t('Est. revenue minus spend')}
                    />
                </div>
            </div>

            <div className="min-w-0 rounded-xl border border-hairline bg-white p-5">
                <Flex
                    justify="space-between"
                    align="center"
                    gap="middle"
                    wrap
                    className="mb-4"
                >
                    <Typography.Title
                        level={5}
                        className="mb-0"
                    >
                        {t('Customers')}
                    </Typography.Title>
                    <Segmented<Filter>
                        value={filter}
                        onChange={setFilter}
                        options={[
                            { label: t('All ({{count}})', { count: companies.length }), value: 'all' },
                            ...(['loss', 'near_limit', 'inactive'] as const).map((flag) => ({
                                label: `${flagLabels[flag]} (${count(flag)})`,
                                value: flag,
                            })),
                        ]}
                    />
                </Flex>
                <Table<CustomerRow>
                    size="small"
                    rowKey="id"
                    loading={isPending}
                    dataSource={rows}
                    scroll={{ x: 'max-content' }}
                    pagination={{ pageSize: 50, hideOnSinglePage: true }}
                    columns={[
                        {
                            title: t('Company'),
                            key: 'name',
                            sorter: (a, b) => (a.name ?? '').localeCompare(b.name ?? ''),
                            render: (_, row) => (
                                <Flex
                                    vertical
                                    gap={4}
                                >
                                    <Link
                                        to="/companies/$companyId"
                                        params={{ companyId: row.id }}
                                    >
                                        {row.name ?? t('Unnamed company')}
                                    </Link>
                                    {row.flags.length > 0 && (
                                        <Flex
                                            gap={4}
                                            wrap
                                        >
                                            {row.flags.map((flag) => (
                                                <Tag
                                                    key={flag}
                                                    color={flagColors[flag]}
                                                    className="m-0"
                                                >
                                                    {flagLabels[flag]}
                                                </Tag>
                                            ))}
                                        </Flex>
                                    )}
                                </Flex>
                            ),
                        },
                        {
                            title: t('Plan'),
                            key: 'plan',
                            render: (_, row) => (
                                <Flex
                                    vertical
                                    gap={2}
                                >
                                    <span>{planLabel(row)}</span>
                                    {statusLabels[row.status] && (
                                        <span className="text-xs text-ink-tertiary">{statusLabels[row.status]}</span>
                                    )}
                                </Flex>
                            ),
                        },
                        {
                            title: t('Candidates'),
                            dataIndex: 'candidates',
                            align: 'right',
                            className: 'tabular-nums',
                            sorter: (a, b) => a.candidates - b.candidates,
                        },
                        {
                            title: t('Chats'),
                            dataIndex: 'chats',
                            align: 'right',
                            className: 'tabular-nums',
                            sorter: (a, b) => a.chats - b.chats,
                        },
                        {
                            title: (
                                <Tooltip title={t('In the current billing period')}>
                                    <span>{t('Usage')}</span>
                                </Tooltip>
                            ),
                            key: 'usage',
                            sorter: (a, b) => usageShare(a) - usageShare(b),
                            render: (_, row) => (
                                <Flex
                                    vertical
                                    gap={2}
                                    className="text-xs tabular-nums"
                                >
                                    {row.conversations && (
                                        <span>
                                            {t('{{used}} / {{limit}} candidates', {
                                                used: row.conversations.used.toLocaleString(),
                                                limit: row.conversations.limit.toLocaleString(),
                                            })}
                                        </span>
                                    )}
                                    {row.messages && (
                                        <span>
                                            {t('{{used}} / {{limit}} chat messages', {
                                                used: row.messages.used.toLocaleString(),
                                                limit: row.messages.limit.toLocaleString(),
                                            })}
                                        </span>
                                    )}
                                    {!row.conversations && !row.messages && '—'}
                                </Flex>
                            ),
                        },
                        {
                            title: t('Spend'),
                            dataIndex: 'costEurMicros',
                            align: 'right',
                            className: 'tabular-nums',
                            defaultSortOrder: 'descend',
                            sorter: (a, b) => a.costEurMicros - b.costEurMicros,
                            render: (micros: number) => formatMicros(micros, 'EUR'),
                        },
                        {
                            title: t('Est. revenue'),
                            dataIndex: 'revenue',
                            align: 'right',
                            className: 'tabular-nums',
                            sorter: (a, b) => (a.revenue ?? 0) - (b.revenue ?? 0),
                            render: (revenue: number | null) =>
                                revenue === null ? '—' : formatMicros(centsToMicros(revenue), 'EUR'),
                        },
                        {
                            title: t('Est. margin'),
                            key: 'margin',
                            align: 'right',
                            className: 'tabular-nums',
                            sorter: (a, b) => (margin(a) ?? 0) - (margin(b) ?? 0),
                            render: (_, row) => {
                                const value = margin(row)

                                return value === null ? '—' : formatMicros(value, 'EUR')
                            },
                        },
                        {
                            title: t('Last active'),
                            dataIndex: 'lastActiveAt',
                            sorter: (a, b) => (a.lastActiveAt?.getTime() ?? 0) - (b.lastActiveAt?.getTime() ?? 0),
                            render: (date: Date | null) =>
                                date ? (
                                    <TimeAgo date={date} />
                                ) : (
                                    <span className="text-ink-tertiary">{t('Never')}</span>
                                ),
                        },
                    ]}
                />
                {data && (
                    <Typography.Text
                        type="secondary"
                        className="mt-3 block text-xs"
                    >
                        {t(
                            'Near a limit: {{share}}% or more of the candidates or chat messages in the plan. Inactive: no login, candidate or chat in {{days}} days.',
                            { share: Math.round(data.nearLimit * 100), days: data.inactiveDays }
                        )}
                    </Typography.Text>
                )}
            </div>
        </Flex>
    )
}
